import { useCallback, useLayoutEffect, useRef } from 'react'

// Prevents Server side error
export const useUniversalLayoutEffect = typeof document === 'undefined' ? () => {} : useLayoutEffect

/**
 * Returns a stable ref that always points to the latest value.
 * @param value
 *
 * @example
 * const MyComponent = () => {
 *   const [count, setCount] = useState(0)
 *   const countRef = useLatestRef(count)
 *
 *   useEffect(() => {
 *     const interval = setInterval(() => {
 *       console.log(countRef.current)
 *     }, 1000)
 *     return () => clearInterval(interval)
 *   }, [])
 *
 *   return <button onClick={() => setCount(count + 1)}>Increment</button>
 * }
 *
 */
export function useLatestRef<T>(value: T): { current: T } {
  // Store the value in a ref
  const valueRef = useRef(value)

  // Update the ref to point to the new value on each render
  useUniversalLayoutEffect(() => {
    valueRef.current = value
  }, [value])

  // Return a stable ref
  return valueRef
}

/**
 * Returns a stable callback that will always call the latest version of the handler.
 * @param handler
 *
 * @example
 * const MyComponent = () => {
 *  const handleClick = useLatestCallbackRef(() => {
 *    console.log('Clicked!')
 *  })
 *  return <button onClick={handleClick}>Click me</button>
 * }
 */
export function useLatestCallbackRef<T extends (...args: any[]) => any>(handler: T): T {
  // Store the handler in a ref
  const handlerRef = useLatestRef(handler)

  // Return a stable function
  return useCallback((...args: Parameters<T>) => {
    // Call the current handler with all arguments
    return handlerRef.current?.(...args)
  }, []) as T
}
